import React, { useEffect, useRef, useState } from "react";
import Flag from "react-world-flags";
import { translate } from "../../localization/translateService";
import { getLanguageOptions } from "../../services/languageService";
import ResponsiveImage from "./ResponsiveImage";

const Instruction = ({ props }) => {
  const [languageOptions, setLanguageOptions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchLanguageOptions = async () => {
      try {
        const options = await getLanguageOptions();

        const translationPromises = options.map(async (option) => {
          const translatedName = await translate(option.name, true);
          return {
            ...option,
            name: translatedName,
          };
        });

        const translatedOptions = await Promise.all(translationPromises);
        setLanguageOptions(translatedOptions);
      } catch (error) {
        console.error("Error fetching language options:", error);
      }
    };

    fetchLanguageOptions();
  }, []);

  const buildLanguagePath = (lang) => {
    if (lang === "en") return "/en/instruction";
    if (lang === "vi") return "/vi/instruction";
    if (lang === "ko") return "/ko/instruction";
    return "/en/instruction";
  };

  // Get current language from URL (fallback to 'en')
  useEffect(() => {
    // Parse language segment from path
    const segments = window.location.pathname.split("/").filter(Boolean);
    const first = (segments[0] || "en").toLowerCase();
    if (["en", "vi", "ko"].includes(first)) {
      setSelectedLanguage(first);
    } else {
      setSelectedLanguage("en");
    }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const getLanguageTitle = (value) => {
    return languageOptions.find((item) => item.value === value)?.name || "";
  };

  const getLanguageCode = (value) => {
    return languageOptions.find((item) => item.value === value)?.code || "US";
  };

  const handleChangeLanguage = (value) => {
    setDropdownOpen(false);
    setSelectedLanguage(value);
    // Navigate to language-specific path
    window.location.href = buildLanguagePath(value);
  };

  return (
    <div className="instruction-page">
      <div className="instruction-page-container flex flex-column">
        <div className="first-section">
          <div
            className="flex flex-column justify-content-center w-9rem absolute z-5"
            style={{
              top: "1rem",
              right: "0.5rem",
            }}
            ref={dropdownRef}
          >
            <div
              className="flex align-items-center justify-content-between gap-2 px-2 py-1 surface-0 border-round-sm shadow-2 cursor-pointer"
              onClick={() => setDropdownOpen((o) => !o)}
            >
              <div className="flex align-items-center gap-2">
                <Flag
                  code={getLanguageCode(selectedLanguage)}
                  style={{ width: 20, height: 14 }}
                />
                <span className="text-800">
                  {getLanguageTitle(selectedLanguage) || "언어"}
                </span>
              </div>
              <i
                className={`pi ${
                  dropdownOpen ? "pi-angle-up" : "pi-angle-down"
                }`}
              ></i>
            </div>
            {dropdownOpen && (
              <div className="surface-0 border-round-sm shadow-3 mt-2 w-full">
                <ul className="list-none m-0 p-2 flex flex-column gap-1">
                  {languageOptions.map((option) => (
                    <li
                      key={option.value}
                      className={`flex align-items-center gap-2 px-2 py-2 cursor-pointer border-round-sm ${
                        option.value === selectedLanguage
                          ? "bg-yellow-100"
                          : "hover:surface-100"
                      }`}
                      onClick={() => handleChangeLanguage(option.value)}
                    >
                      <Flag
                        code={option.code}
                        style={{ width: 20, height: 14 }}
                      />
                      <span>{option.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <ResponsiveImage
            src={props?.firstSectionImage}
            mobileSrc={props?.firstSectionImageMobile}
            alt="first-section-image"
          />
          <div className={`first-section-button spacing-${selectedLanguage}`}>
            <a href="tutor-onboarding?isOpenForm=true" className="no-underline">
              {props?.firstSectionButtonLabel}
              <svg
                width="70"
                height="70"
                viewBox="0 0 70 70"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute right-0 w-2rem"
                style={{
                  bottom : "-35px"
                }}
              >
                <path
                  d="M29.2568 2C30.3962 2 31.4893 2.45312 32.2949 3.25879C33.1004 4.06436 33.5526 5.15672 33.5527 6.2959V8.59277C33.5526 9.73194 33.1004 10.8243 32.2949 11.6299C31.4893 12.4356 30.3962 12.8887 29.2568 12.8887C29.0374 12.8887 28.8198 12.8708 28.6055 12.8379L63.458 24.2188L63.4648 24.2207L63.4717 24.2236C64.7322 24.6453 65.8358 25.438 66.6377 26.498C67.4395 27.558 67.9019 28.8355 67.9648 30.1631C68.0277 31.4906 67.6883 32.8066 66.9902 33.9375C66.292 35.0685 65.2678 35.9622 64.0527 36.501L64.0234 36.5137L63.9951 36.5264L63.9033 36.5635L63.8848 36.5703L63.8672 36.5771L52.3428 41.0029L66.4014 55.0596H66.4004C66.9071 55.565 67.3095 56.1652 67.584 56.8262C67.8587 57.4879 68 58.1976 68 58.9141C68 59.6305 67.8587 60.3403 67.584 61.002C67.3093 61.6633 66.9056 62.2629 66.3984 62.7686L66.3994 62.7695L62.7578 66.4033L62.7568 66.4023C62.2519 66.9079 61.6531 67.31 60.9932 67.584C60.3314 67.8588 59.6218 68 58.9053 68C58.1887 68 57.4791 67.8588 56.8174 67.584C56.1557 67.3092 55.5546 66.906 55.0488 66.3984V66.3975L40.9961 52.3428L36.6133 63.7744L36.6123 63.7773C36.5685 63.9154 36.5193 64.0208 36.4941 64.0723C36.487 64.0868 36.4784 64.1012 36.4717 64.1143C35.9621 65.2575 35.1371 66.232 34.0898 66.9199C33.0185 67.6236 31.7641 67.9961 30.4824 67.9932V67.9941H30.2139L30.1709 67.9922C28.8321 67.9354 27.5427 67.4693 26.4775 66.6562C25.4157 65.8457 24.6276 64.7295 24.2188 63.458L12.8379 28.6055C12.8708 28.8198 12.8887 29.0374 12.8887 29.2568C12.8887 30.3962 12.4356 31.4893 11.6299 32.2949C10.8243 33.1004 9.73194 33.5526 8.59277 33.5527H6.2959C5.15672 33.5526 4.06436 33.1004 3.25879 32.2949C2.45312 31.4893 2 30.3962 2 29.2568C2.00007 28.1176 2.45321 27.0253 3.25879 26.2197C4.06437 25.4141 5.15664 24.961 6.2959 24.9609H8.59277C9.73202 24.961 10.8243 25.4142 11.6299 26.2197C11.9443 26.5342 12.2043 26.8926 12.4053 27.2803L9.2168 17.5137L9.21387 17.5029C8.8442 16.3495 8.7989 15.1166 9.08398 13.9395C9.3691 12.7622 9.97358 11.6866 10.8301 10.8301C11.6866 9.97358 12.7622 9.3691 13.9395 9.08398C15.0432 8.81669 16.1959 8.83987 17.2861 9.14844L17.5029 9.21387L17.5137 9.2168L27.2803 12.4053C26.8926 12.2043 26.5342 11.9443 26.2197 11.6299C25.4142 10.8243 24.961 9.73202 24.9609 8.59277V6.2959C24.961 5.15664 25.4141 4.06437 26.2197 3.25879C27.0253 2.45321 28.1176 2.00007 29.2568 2ZM10.583 34.1572C11.1456 34.1171 11.7109 34.188 12.2461 34.3662C12.7813 34.5444 13.2759 34.8269 13.7021 35.1963C14.1285 35.5658 14.478 36.016 14.7305 36.5205C14.9829 37.0249 15.1336 37.5741 15.1738 38.1367C15.214 38.6995 15.1431 39.2655 14.9648 39.8008C14.7866 40.3359 14.5042 40.8306 14.1348 41.2568C13.8114 41.6299 13.4263 41.9437 12.9971 42.1855L12.8105 42.2852L8.21777 44.5801L8.21875 44.5811C7.19974 45.0909 6.01955 45.1755 4.93848 44.8154C3.85751 44.4554 2.96393 43.6801 2.4541 42.6611C1.94439 41.6423 1.85989 40.4627 2.21973 39.3818C2.57975 38.3008 3.35502 37.4063 4.37402 36.8965L8.96582 34.6006C9.47038 34.3481 10.0202 34.1974 10.583 34.1572ZM39.3818 2.21973C40.4627 1.85989 41.6423 1.94439 42.6611 2.4541C43.6801 2.96393 44.4554 3.85751 44.8154 4.93848C45.1755 6.01955 45.0909 7.19974 44.5811 8.21875L42.2852 12.8086C42.033 13.3138 41.6842 13.7647 41.2578 14.1348C40.8314 14.5048 40.3364 14.7873 39.8008 14.9658C39.2651 15.1444 38.6989 15.216 38.1357 15.1758C37.6431 15.1406 37.161 15.02 36.71 14.8213L36.5186 14.7314C36.014 14.4788 35.5638 14.1297 35.1943 13.7031C34.8248 13.2765 34.5433 12.7807 34.3652 12.2451C34.1872 11.7096 34.1166 11.144 34.1572 10.5811C34.1978 10.0183 34.3487 9.4692 34.6016 8.96484L36.8965 4.37402L36.9971 4.18555C37.5193 3.25959 38.3684 2.55722 39.3818 2.21973Z"
                  fill="#3FB390"
                  stroke="white"
                  stroke-width="4"
                />
              </svg>
            </a>
          </div>
        </div>
        <ResponsiveImage
          src={props?.secondSectionImage}
          mobileSrc={props?.secondSectionImageMobile}
          alt="second-section-image"
        />
        <div className="vod-background-section">
          <ResponsiveImage
            src={props?.vodBackground}
            mobileSrc={props?.vodBackgroundMobile}
            alt="vod-background-image"
          />
          <div className="vod-content flex flex-column gap-3">
            <span className="hidden md:block">{props?.vodContent}</span>
            <div className="vod-card-container flex gap-2 md:gap-4 align-items-center">
              <div className="vod-card">
                <div className="ytb-frame">
                  <iframe
                    width="630px"
                    height="auto"
                    src="https://www.youtube.com/embed/ZeAfWIk7Vwc"
                    title="Dotore học tiếng Hàn VOD - Cuộc sống trường học"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerpolicy="strict-origin-when-cross-origin"
                    allowfullscreen
                  ></iframe>
                </div>
                <div className="vod-card-content">
                  <span className="text-left">
                    {props?.schoolLife}{" "}
                    <span className="text-xs font-normal">
                      {props?.instructor} Ms.Lucy
                    </span>
                  </span>
                  <svg
                    width="60"
                    height="60"
                    viewBox="0 0 60 60"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={() =>
                      window.open(
                        "https://www.youtube.com/watch?v=ZeAfWIk7Vwc",
                        "_blank"
                      )
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <rect
                      width="60"
                      height="60"
                      rx="30"
                      fill="#8D8D8D"
                      fill-opacity="0.53"
                    />
                    <path
                      d="M45 30C45.0008 30.3918 44.9118 30.7771 44.7415 31.1185C44.5713 31.46 44.3256 31.7459 44.0284 31.9485L25.6091 44.6604C25.2985 44.8749 24.9429 44.992 24.5788 44.9996C24.2147 45.0072 23.8554 44.905 23.5381 44.7036C23.2237 44.5053 22.9619 44.2162 22.7794 43.8659C22.597 43.5156 22.5005 43.1167 22.5 42.7104V17.2896C22.5005 16.8833 22.597 16.4844 22.7794 16.1341C22.9619 15.7838 23.2237 15.4947 23.5381 15.2964C23.8554 15.095 24.2147 14.9928 24.5788 15.0004C24.9429 15.008 25.2985 15.1251 25.6091 15.3396L44.0284 28.0515C44.3256 28.2541 44.5713 28.54 44.7415 28.8815C44.9118 29.2229 45.0008 29.6082 45 30Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
              <div className="vod-card">
                <div className="ytb-frame">
                  <iframe
                    width="630px"
                    height="auto"
                    src="https://www.youtube.com/embed/fa561m9rzQI"
                    title="Dotore học tiếng Hàn VOD - Sinh hoạt hằng ngày"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerpolicy="strict-origin-when-cross-origin"
                    allowfullscreen
                  ></iframe>
                </div>
                <div className="vod-card-content">
                  <span className="text-left">
                    {props?.dailyRoutine}{" "}
                    <span className="text-xs font-normal">
                      {props?.instructor} Ms.Olivia
                    </span>
                  </span>
                  <svg
                    width="60"
                    height="60"
                    viewBox="0 0 60 60"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={() =>
                      window.open(
                        "https://www.youtube.com/watch?v=fa561m9rzQI&t",
                        "_blank"
                      )
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <rect
                      width="60"
                      height="60"
                      rx="30"
                      fill="#8D8D8D"
                      fill-opacity="0.53"
                    />
                    <path
                      d="M45 30C45.0008 30.3918 44.9118 30.7771 44.7415 31.1185C44.5713 31.46 44.3256 31.7459 44.0284 31.9485L25.6091 44.6604C25.2985 44.8749 24.9429 44.992 24.5788 44.9996C24.2147 45.0072 23.8554 44.905 23.5381 44.7036C23.2237 44.5053 22.9619 44.2162 22.7794 43.8659C22.597 43.5156 22.5005 43.1167 22.5 42.7104V17.2896C22.5005 16.8833 22.597 16.4844 22.7794 16.1341C22.9619 15.7838 23.2237 15.4947 23.5381 15.2964C23.8554 15.095 24.2147 14.9928 24.5788 15.0004C24.9429 15.008 25.2985 15.1251 25.6091 15.3396L44.0284 28.0515C44.3256 28.2541 44.5713 28.54 44.7415 28.8815C44.9118 29.2229 45.0008 29.6082 45 30Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="third-section flex flex-column">
          <ResponsiveImage
            src={props?.thirdSectionImage}
            mobileSrc={props?.thirdSectionImageMobile}
            alt="third-section-image"
          />
          <div className="third-section-button">
            <a href="tutor-onboarding?isOpenForm=true" className="no-underline">
              <span className="flex justify-content-cente gap-3">
                <span className="white-space-nowrap" style={{ fontSize: "23px" }} >
                  {props?.secondSectionButtonLabel}
                </span>
                <svg
                  width="37"
                  height="23"
                  viewBox="0 0 37 23"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mt-1"
                >
                  <path
                    d="M2.2124 10.3447C1.38398 10.3447 0.712402 11.0163 0.712402 11.8447C0.712402 12.6732 1.38397 13.3447 2.2124 13.3447L2.2124 11.8447L2.2124 10.3447ZM35.9231 12.9054C36.5089 12.3196 36.5089 11.3699 35.9231 10.7841L26.3772 1.23813C25.7914 0.652342 24.8416 0.652342 24.2558 1.23813C23.67 1.82391 23.67 2.77366 24.2558 3.35945L32.7411 11.8447L24.2558 20.33C23.67 20.9158 23.67 21.8655 24.2558 22.4513C24.8416 23.0371 25.7914 23.0371 26.3771 22.4513L35.9231 12.9054ZM2.2124 11.8447L2.2124 13.3447L34.8624 13.3447L34.8624 11.8447L34.8624 10.3447L2.2124 10.3447L2.2124 11.8447Z"
                    fill="white"
                  />
                </svg>
              </span>
              <svg
                width="80"
                height="80"
                viewBox="0 0 104 111"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="hidden md:block"
              >
                <path
                  d="M99.2255 54.0749C105.478 76.5467 92.7588 100.241 70.2984 106.761L70.2996 106.762C64.8594 108.355 59.1549 108.839 53.5239 108.188C47.8928 107.536 42.4494 105.76 37.517 102.966L37.4881 102.95L37.459 102.932C30.6566 98.8354 22.5884 90.9756 16.348 84.3284C13.1962 80.9712 10.4547 77.8628 8.50159 75.5935C7.52469 74.4584 6.74384 73.5314 6.20581 72.8872C5.93684 72.5651 5.72847 72.3131 5.58667 72.1411C5.5201 72.0603 5.46834 71.9967 5.4317 71.952C4.51232 71.0187 3.77949 69.9173 3.28038 68.7048C2.75902 67.438 2.50062 66.0784 2.52024 64.7086C2.53988 63.3386 2.83765 61.9864 3.39529 60.7349C3.95101 59.4877 4.75324 58.3659 5.75385 57.437L6.14069 57.0932C10.2035 53.6636 16.1945 54.1658 19.8774 57.784L34.1629 71.8209L34.1758 71.8346L34.1894 71.847C34.2448 71.9031 34.3153 71.9417 34.3925 71.9577C34.4697 71.9737 34.5498 71.9667 34.623 71.9372L34.6347 71.9319C34.893 71.7855 35.0997 71.5615 35.2221 71.289C35.3587 70.9849 35.3821 70.6417 35.2885 70.3218L19.9955 15.3579C19.6272 14.0385 19.53 12.6581 19.711 11.3003C19.8921 9.94149 20.3479 8.63354 21.0498 7.45602C21.7517 6.2785 22.6856 5.25577 23.7947 4.45008C24.9036 3.64452 26.1646 3.07252 27.5012 2.76904L27.5005 2.77024C32.8937 1.53784 38.117 4.98682 39.5482 10.1305L48.685 42.969C48.7328 43.1421 48.8158 43.3038 48.9286 43.4436C49.0412 43.583 49.181 43.6981 49.3398 43.7812C49.4591 43.8437 49.5874 43.8875 49.7195 43.9108L49.8528 43.9274L49.9872 43.9295C50.1139 43.9256 50.2391 43.9025 50.3598 43.8623C50.6778 43.7367 50.9396 43.4993 51.093 43.1931C51.2503 42.8789 51.2827 42.5166 51.184 42.1794L49.1647 34.9219C48.7963 33.6026 48.6992 32.2222 48.8801 30.8644C49.0612 29.5056 49.5171 28.1976 50.2189 27.0201C50.9208 25.8425 51.8547 24.8198 52.9638 24.0141C54.0715 23.2095 55.3305 22.6372 56.6654 22.3335C62.0594 21.0992 67.2845 24.5494 68.7161 29.6939L71.7262 40.5124C71.7739 40.6854 71.857 40.8472 71.9697 40.9869C72.0824 41.1266 72.2229 41.2412 72.3819 41.3244C72.541 41.4076 72.7159 41.4576 72.8949 41.4705C73.0667 41.4829 73.2387 41.4598 73.4019 41.4055C73.7197 41.2799 73.9807 41.0425 74.1341 40.7365C74.2916 40.4223 74.3248 40.0598 74.2261 39.7226L74.2207 39.7068L73.2869 36.3509C71.8534 31.1987 74.5582 25.5697 79.7959 23.8265L79.8006 23.8252C81.0999 23.3954 82.4729 23.234 83.8365 23.35C85.2003 23.4661 86.5267 23.8572 87.7348 24.5005C88.9429 25.1437 90.0079 26.0254 90.8654 27.0921C91.7229 28.1588 92.3549 29.3887 92.7236 30.7067L99.2255 54.0749Z"
                  fill="#FFA12F"
                  stroke="white"
                  stroke-width="5"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Instruction;
